import * as React from "react";
import {
  FieldRow,
  LabeledCheckbox,
  LabeledInput,
  SectionCard,
} from "../../components/forms/formPrimitives";
import { BRAND, REFINED } from "../../components/risikoanalyseStep/tokens";
import type { ProfileSectionProps } from "./profileShell";
import { loadSystemcheckSchedule, loadTwvSchedule } from "./scheduleApi";
import {
  SYSTEMCHECK_WINDOW,
  WEEKDAYS,
  type SystemcheckSchedule,
  type TwvSchedule,
} from "./scheduleTypes";

/**
 * Sektion „Termine" der Profil-Seite (SP6) — vollständig DISABLED & read-only.
 *
 * Zeigt zwei Blöcke, die später aktiviert werden (E6, Admin/Mail-gesteuert):
 *  - TWV-Blocker („Wann nimmst du dir Zeit für Wiedervorlagen?"): Wochentage
 *    Mo–Fr + Von/Bis.
 *  - Systemcheck („Wann findet der Systemcheck vor dem Login statt?"): Von/Bis.
 *
 * Der gesamte interaktive Inhalt steckt in einem nativen `<fieldset disabled>`
 * (deaktiviert alle inneren `<input>`, da `LabeledInput`/`LabeledSelect` KEIN
 * `disabled`-Prop tragen — Konsistenz P2) plus `opacity:.6` + `pointer-events:
 * none` für die Optik (§6). KEIN Save, KEIN PUT.
 *
 * Vor-Befüllung kommt read-only aus den UserPreference-Scopes
 * `profile.schedule.twv` / `…systemcheck` (E1, migrationsfrei). Default LEER —
 * NIEMALS mit den hartcodierten PFLICHT_BLOCK_DEFS-Zeiten (16:00–16:30) o. Ä.
 * vorbelegen (E6 / R2).
 *
 * `sectionRef` wird vom SP1-`ProfilePage`-Wrapper getragen (Scroll-Spy), daher
 * hier — wie bei den übrigen Sektionen — bewusst NICHT erneut angeheftet.
 */

const DISABLED_FIELDSET: React.CSSProperties = {
  border: "none",
  margin: 0,
  padding: 0,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  rowGap: "20px",
  opacity: 0.6,
  pointerEvents: "none",
};

const QUESTION_LABEL: React.CSSProperties = {
  margin: 0,
  fontFamily: REFINED.fontUI,
  fontWeight: 600,
  fontSize: "0.95rem",
  color: REFINED.ink,
};

const WEEKDAY_ROW: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  alignItems: "center",
};

/** Dezente Info-Box (außerhalb des disabled-Wrappers → bleibt lesbar). */
const InfoBox: React.FC = () => (
  <div
    style={{
      backgroundColor: REFINED.aub50,
      border: `1px solid ${REFINED.line}`,
      borderRadius: "12px",
      padding: "14px 16px",
      fontFamily: REFINED.fontUI,
      fontSize: "0.9rem",
      lineHeight: 1.4,
      color: REFINED.ink2,
    }}
  >
    Diese Einstellungen werden später aktiviert. Du erhältst dann eine E-Mail.
  </div>
);

/** Amber/Gold-Hinweis zur Outlook-Sync-Abhängigkeit (außerhalb des disabled-Wrappers). */
const AmberHint: React.FC = () => (
  <div
    style={{
      background: "rgba(247,168,35,0.12)",
      borderLeft: `3px solid ${BRAND.gold}`,
      borderRadius: "8px",
      padding: "12px 14px",
      fontFamily: REFINED.fontUI,
      fontSize: "0.875rem",
      lineHeight: 1.4,
      color: REFINED.ink2,
    }}
  >
    Hinweis: Die Synchronisierung mit Outlook (Serientermine) hängt vom
    kommenden Kalender-Update ab.
  </div>
);

export const ProfileTermine: React.FC<ProfileSectionProps> = () => {
  const [twv, setTwv] = React.useState<TwvSchedule>({});
  const [systemcheck, setSystemcheck] = React.useState<SystemcheckSchedule>({});
  // loading getrackt, aber bewusst kein Spinner (Felder sind disabled/leer;
  // stiller Fill genügt — §4.4 / M4).
  const [, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    // Read-only: niemals schreiben. allSettled → ein fehlschlagender Loader
    // killt den anderen nicht (§6 Edge-Case 5).
    Promise.allSettled([loadTwvSchedule(), loadSystemcheckSchedule()]).then(
      ([twvRes, sysRes]) => {
        if (!mounted) return;
        if (twvRes.status === "fulfilled") setTwv(twvRes.value);
        if (sysRes.status === "fulfilled") setSystemcheck(sysRes.value);
        setLoading(false);
      },
    );
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <SectionCard title="TERMINE" heading="Termine">
      {/* Hinweise außerhalb des disabled-Wrappers → bleiben voll lesbar (R3). */}
      <InfoBox />
      <AmberHint />

      {/* Gesamter interaktiver Inhalt deaktiviert: natives fieldset disabled
          (deaktiviert alle inneren Inputs) + Optik via opacity/pointer-events. */}
      <fieldset disabled style={DISABLED_FIELDSET}>
          {/* ── TWV-Blocker ─────────────────────────────────────────── */}
          <FieldRow columns={1}>
            <p style={QUESTION_LABEL}>Wann nimmst du dir Zeit für Wiedervorlagen?</p>
            <div style={WEEKDAY_ROW}>
              {WEEKDAYS.map((d) => (
                <LabeledCheckbox
                  key={d.id}
                  label={d.label}
                  // Read-only: aus dem Scope gelesen, onChange no-op, disabled.
                  checked={twv.weekdays?.includes(d.id) ?? false}
                  onChange={() => {}}
                  disabled
                />
              ))}
            </div>
          </FieldRow>

          <FieldRow columns={2}>
            <LabeledInput
              label="Von"
              name="twv-from"
              type="text"
              value={twv.from ?? ""}
              onChange={() => {}}
              placeholder="z. B. 14:00"
            />
            <LabeledInput
              label="Bis"
              name="twv-to"
              type="text"
              value={twv.to ?? ""}
              onChange={() => {}}
              placeholder="z. B. 16:00"
            />
          </FieldRow>

          {/* ── Systemcheck / To-Do-Check ───────────────────────────── */}
          <FieldRow columns={1}>
            <p style={QUESTION_LABEL}>Wann findet der Systemcheck vor dem Login statt?</p>
          </FieldRow>

          <FieldRow columns={2}>
            {/* TODO(Aktivierung): Eingabe gegen SYSTEMCHECK_WINDOW (14–17 Uhr)
                validieren. Placeholder spiegelt die Invariante, ist aber KEIN
                Default-Wert — Felder bleiben leer, wenn der Scope leer ist (E6). */}
            <LabeledInput
              label="Von"
              name="systemcheck-from"
              type="text"
              value={systemcheck.from ?? ""}
              onChange={() => {}}
              placeholder={SYSTEMCHECK_WINDOW.min}
            />
            <LabeledInput
              label="Bis"
              name="systemcheck-to"
              type="text"
              value={systemcheck.to ?? ""}
              onChange={() => {}}
              placeholder={SYSTEMCHECK_WINDOW.max}
            />
          </FieldRow>
      </fieldset>
    </SectionCard>
  );
};

export default ProfileTermine;
